import type {GridItem} from './types';
import type {Feature} from '@/services/OverlaysService';
import type {DisplayField} from '@/lib/classes/InfoWindowContent/DisplayField';

/**
 * Column-deletion primitives extracted from FeatureGrid so the deletion flow is modular and testable
 */

/**
 * Removes a display field from an item's in-memory displayFields array. Does not persist; call
 * deleteDisplayFieldInDatabase separately for that.
 * @returns the removed DisplayField, or null if the item has no such field.
 * @throws if the field exists but is not deletable, matching Feature._findAndRemoveDisplayField.
 */
export function deleteDisplayFieldInMemory(item: GridItem, columnName: string): DisplayField | null {
  const displayFields = item.data.displayFields;
  if (!displayFields) return null;

  const fieldIndex = displayFields.findIndex((df) => df.label === columnName);
  if (fieldIndex === -1) {
    console.warn(`Display field "${columnName}" not found in item:`, item);
    return null;
  }

  if (displayFields[fieldIndex].allowDelete === false) {
    throw new Error(`Field "${columnName}" cannot be deleted.`);
  }

  const [removedField] = displayFields.splice(fieldIndex, 1);
  return removedField;
}

/**
 * Persists deletion of a display field to the database via the owning Feature.
 * @throws if the item's feature cannot be resolved from featuresForGrid.
 */
export async function deleteDisplayFieldInDatabase(
  item: GridItem,
  columnName: string,
  featuresForGrid: Map<string, Feature>,
): Promise<void> {
  const featureId = item.type === 'feature' ? item.data.id : item.parent?.id;
  if (!featureId) {
    throw new Error('Could not find featureId for item');
  }

  const feature = featuresForGrid.get(featureId);
  if (!feature) {
    throw new Error(`Could not find feature with id ${featureId}`);
  }

  if (item.type === 'feature') {
    await feature.deleteDisplayField(columnName);
  } else {
    await feature.deleteDisplayField(item.data.id, columnName);
  }
}
